import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { downloadFile } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import CustomerLedgerSheet from "@/components/CustomerLedgerSheet";
import { FileSpreadsheet, FileText, Search, BookOpen, Printer } from "lucide-react";

const TABS = [
  { key: "customer-wise", label: "Customer-wise" },
  { key: "area-wise", label: "Area Sales" },
  { key: "driver-wise", label: "Delivery Person" },
  { key: "outstanding", label: "Outstanding" },
  { key: "returns", label: "Returns & Mismatch" },
  { key: "cancelled", label: "Cancelled" },
  { key: "sales", label: "Sales Summary" },
  { key: "customer360", label: "Customer 360" },
];

const isNumeric = (v) => typeof v === "number";

export default function Reports() {
  const [tab, setTab] = useState("customer-wise");
  const [salesPeriod, setSalesPeriod] = useState("daily");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [area, setArea] = useState("all");
  const [sort, setSort] = useState("default");
  const [custSearch, setCustSearch] = useState("");
  const [ledgerFor, setLedgerFor] = useState(null);

  const reportKey = tab === "sales" ? `sales-${salesPeriod}` : tab;
  const isCustomer360 = tab === "customer360";
  const params = { date_from: dateFrom, date_to: dateTo, area, sort };
  const qs = new URLSearchParams(params).toString();

  const { data: areas = [] } = useQuery({ queryKey: ["areas"], queryFn: async () => (await api.get("/customers/areas")).data });

  const { data, isLoading } = useQuery({
    queryKey: ["report", reportKey, dateFrom, dateTo, area, sort],
    queryFn: async () => (await api.get(`/reports/${reportKey}`, { params })).data,
    enabled: !isCustomer360,
  });

  const { data: customers = [] } = useQuery({
    queryKey: ["customers-360", custSearch],
    queryFn: async () => (await api.get("/customers", { params: { search: custSearch } })).data,
    enabled: isCustomer360,
  });

  const doExport = async (fmt) => {
    try {
      await downloadFile(`/reports/${reportKey}/export?format=${fmt}&${qs}`, `${reportKey}.${fmt === "pdf" ? "pdf" : "xlsx"}`);
      toast.success(`Exported ${fmt.toUpperCase()}`);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Reports</h1>
          <p className="mt-1 text-sm text-muted-foreground">Filter, sort &amp; export every view to PDF / Excel</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.print()} data-testid="report-print"><Printer className="mr-2 h-4 w-4" /> Print</Button>
          {!isCustomer360 && (
            <>
              <Button variant="outline" onClick={() => doExport("excel")} data-testid="report-export-excel"><FileSpreadsheet className="mr-2 h-4 w-4" /> Excel</Button>
              <Button variant="outline" onClick={() => doExport("pdf")} data-testid="report-export-pdf"><FileText className="mr-2 h-4 w-4" /> PDF</Button>
            </>
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex h-auto flex-wrap justify-start gap-1">
          {TABS.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`report-tab-${t.key}`}>{t.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      {(!isCustomer360 || true) && (
        <Card className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-5">
          <div className="space-y-1"><label className="text-[10px] font-mono uppercase text-muted-foreground">From</label><Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} disabled={isCustomer360} data-testid="report-date-from" /></div>
          <div className="space-y-1"><label className="text-[10px] font-mono uppercase text-muted-foreground">To</label><Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} disabled={isCustomer360} data-testid="report-date-to" /></div>
          <div className="space-y-1">
            <label className="text-[10px] font-mono uppercase text-muted-foreground">Area</label>
            <Select value={area} onValueChange={setArea} disabled={isCustomer360}><SelectTrigger data-testid="report-area-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Areas</SelectItem>{areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="space-y-1">
            <label className="text-[10px] font-mono uppercase text-muted-foreground">Sort</label>
            <Select value={sort} onValueChange={setSort} disabled={isCustomer360}><SelectTrigger data-testid="report-sort-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="default">Default</SelectItem><SelectItem value="amount">Amount</SelectItem><SelectItem value="date">Date</SelectItem><SelectItem value="area">Area</SelectItem><SelectItem value="name">Name</SelectItem></SelectContent></Select>
          </div>
          {tab === "sales" && (
            <div className="space-y-1">
              <label className="text-[10px] font-mono uppercase text-muted-foreground">Period</label>
              <Select value={salesPeriod} onValueChange={setSalesPeriod}><SelectTrigger data-testid="sales-period-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent></Select>
            </div>
          )}
        </Card>
      )}

      <div id="report-print">
        <div className="print-show">
          <h2 className="font-display text-xl font-bold">SARAVANA DELAPP</h2>
          <p className="mb-3 text-sm text-muted-foreground">{(isCustomer360 ? "Customer 360" : data?.title) || "Report"} · Generated {new Date().toLocaleString("en-IN")}</p>
        </div>
      {isCustomer360 ? (
        <Card className="p-4">
          <div className="print-hidden relative mb-4 max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search customer by name or ID (SS…)" value={custSearch} onChange={(e) => setCustSearch(e.target.value)} data-testid="customer360-search" />
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-secondary/60"><TableHead>Cust ID</TableHead><TableHead>Customer</TableHead><TableHead>Area</TableHead><TableHead className="text-right">Orders</TableHead><TableHead className="text-right">Outstanding</TableHead><TableHead className="text-right">360° View</TableHead></TableRow></TableHeader>
              <TableBody>
                {customers.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="py-10 text-center text-muted-foreground">No customers</TableCell></TableRow>
                ) : customers.map((c) => (
                  <TableRow key={c.id} className="hover:bg-secondary/40" data-testid={`customer360-row-${c.id}`}>
                    <TableCell className="font-mono text-sm">{c.code}</TableCell>
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{c.area}</TableCell>
                    <TableCell className="text-right font-mono">{c.orders}</TableCell>
                    <TableCell className="text-right font-mono">{c.outstanding}</TableCell>
                    <TableCell className="text-right"><Button variant="outline" size="sm" onClick={() => setLedgerFor(c)} data-testid={`customer360-view-${c.id}`}><BookOpen className="mr-1.5 h-4 w-4" /> View</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="border-b bg-secondary/40 px-5 py-3"><h3 className="font-display font-semibold">{data?.title || "Report"}</h3></div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-secondary/60">{data?.headers?.map((h, i) => <TableHead key={i} className={i >= 3 ? "text-right" : ""}>{h}</TableHead>)}</TableRow></TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={data?.headers?.length || 5} className="py-12 text-center text-muted-foreground">Loading…</TableCell></TableRow>
                ) : !data?.rows?.length ? (
                  <TableRow><TableCell colSpan={data?.headers?.length || 5} className="py-12 text-center text-muted-foreground">No data available</TableCell></TableRow>
                ) : data.rows.map((row, ri) => (
                  <TableRow key={ri} className="hover:bg-secondary/40" data-testid={`report-row-${ri}`}>
                    {row.map((cell, ci) => (
                      <TableCell key={ci} className={`${isNumeric(cell) || ci >= 3 ? "text-right font-mono" : ""} ${ci === 0 ? "font-medium" : ""}`}>
                        {isNumeric(cell) && data.headers[ci]?.includes("₹") ? new Intl.NumberFormat("en-IN").format(cell) : String(cell)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
      </div>

      <CustomerLedgerSheet customer={ledgerFor} open={!!ledgerFor} onClose={() => setLedgerFor(null)} />
    </div>
  );
}
